import { usePropertyUsers } from "./property-users.hooks";
import { Button } from "@/components/ui/button";
import { uuid } from "@/interfaces";
import { AssignUserModal } from "./assign-user-modal";
import { AddContactModal } from "./add-contact-modal";
import { useAdministersActiveOrganization } from "@/hooks/useAdministersActiveOrganization";
import SimpleTable, { ColumnDef } from "@/components/shared/simple-table";
import { PropertyUserDisplayModel } from "@/app/pmc/properties-detail/properties-detail.components/property-users/property-users.entity.ts";
import { BadgeCheckIcon, Pencil, Trash } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog.tsx";
import { useState } from "react";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

interface Props {
  propertyId: uuid;
}

export function PropertyUsersTab({ propertyId }: Props) {
  const {
    users,
    assignableUsers,
    total,
    page,
    pageSize,
    setPage,
    setPageSize,
    removeUser,
    openEditModal,
    assigning,
    closeAssignModal,
    openAssignModal,
    addUser,
    editing,
    closeEditModal,
    updateUser,
    addContact,
    addingContact,
    openAddContactModal,
    closeAddContactModal,
  } = usePropertyUsers(propertyId);
  const administers = useAdministersActiveOrganization();

  const [deleteId, setDeleteId] = useState<string | null>(null);

  const columns: ColumnDef<PropertyUserDisplayModel>[] = [
    {
      header: "Contact",
      accessor: (row) => (
        <div className="flex items-center gap-2">
          <Avatar>
            <AvatarImage src={row.user.image} />
            <AvatarFallback>{row.user.name.charAt(0)}</AvatarFallback>
          </Avatar>
          <div className="flex flex-col">
            <div className="flex items-center gap-2">
              <span>{row.user.name}</span>
              {row.is_primary_contact && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <BadgeCheckIcon className="w-4 h-4 text-white fill-primary" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Primary Contact</p>
                  </TooltipContent>
                </Tooltip>
              )}
            </div>
            <span className="text-muted-foreground text-xs">{row.user.email}</span>
          </div>
        </div>
      ),
    },
    { header: "Role", accessor: (row) => row.meta_data?.role ?? "—" },
    { header: "Notes", accessor: (row) => row.meta_data?.notes ?? "—" },
    {
      header: "Actions",
      accessor: (row) => (
        <div className="flex justify-end gap-2">
          <Button variant="outline" size="sm" aria-label={`Edit ${row.user.name}`} onClick={() => openEditModal(row)}>
            <Pencil className="w-4 h-4" />
          </Button>

          <Dialog open={deleteId === row.id} onOpenChange={(open) => setDeleteId(open ? row.id : null)}>
            <DialogTrigger asChild>
              <Button variant="destructive" size="sm" aria-label={`Unassign ${row.user.name}`}>
                <Trash className="w-4 h-4" />
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Remove User?</DialogTitle>
              </DialogHeader>
              <p className="pt-2 text-sm text-muted-foreground">
                Are you sure you want to unassign <strong>{row.user.name}</strong>?
              </p>
              <DialogFooter className="pt-4">
                <Button variant="outline" onClick={() => setDeleteId(null)}>
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  onClick={async () => {
                    if (await removeUser(row.id)) setDeleteId(null);
                  }}
                >
                  Unassign
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      ),
      className: "justify-end",
    },
  ];

  return (
    <>
      <div className="flex justify-end gap-2 mb-4">
        {administers && (
          <Button variant="outline" onClick={() => openAddContactModal()}>
            + Add Contact
          </Button>
        )}
        <Button onClick={() => openAssignModal()}>+ Assign User</Button>
      </div>

      <SimpleTable<PropertyUserDisplayModel>
        columns={columns}
        data={users}
        totalCount={total}
        page={page}
        pageSize={pageSize}
        setPage={setPage}
        setPageSize={setPageSize}
        sortKey={undefined}
        sortOrder="asc"
        onSortChange={() => {}}
        filters={[]}
        onReset={() => {}}
        onApply={() => {}}
      />

      <AssignUserModal
        open={assigning}
        onOpenChange={(open) => !open && closeAssignModal()}
        onSubmit={async ({ user_id, role, notes, is_primary_contact }) => {
          if (await addUser(user_id, { role, notes }, is_primary_contact)) closeAssignModal();
        }}
        users={assignableUsers}
      />

      <AddContactModal
        open={addingContact}
        onOpenChange={(open) => !open && closeAddContactModal()}
        onSubmit={async ({ email, role, notes, is_primary_contact }) => {
          const added = await addContact(email, { role, notes }, is_primary_contact);
          if (added) closeAddContactModal();
          return added;
        }}
      />

      <AssignUserModal
        open={!!editing}
        onOpenChange={(open) => !open && closeEditModal()}
        onSubmit={async ({ role, notes, is_primary_contact }) => {
          if (editing) {
            await updateUser(editing.id, { role, notes }, is_primary_contact);
          }
        }}
        users={editing?.user ? [editing.user] : []}
        initialValues={{
          user_id: editing?.user.id || "",
          role: editing?.meta_data?.role || "",
          notes: editing?.meta_data?.notes || "",
          is_primary_contact: editing?.is_primary_contact || false,
        }}
        disableUserField
      />
    </>
  );
}
